/**
 * Details step — split layout: Calendar (left 60%) | Form (right 40%).
 */
import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Box, Flex } from '@chakra-ui/react';
import type { FormBuilderHandle, CalendarHandle } from '@kula-ai/components';
import type { StepFormApi } from '@/hooks/useStepValidation';
import { useShowToast } from '@/hooks';
import { useKFlags } from '@/hooks/use-k-flags';
import type { ScheduleStepProps } from '../../config/types';
import { isSelfScheduleMode } from '../../config/modeClassifiers';
import { DetailsForm } from './DetailsForm';
import { useScheduleSharedState } from '../../state/useScheduleSharedState';
import { CalendarPanel } from '../../calendar/CalendarPanel';
import { NoSlotsAvailablePopover } from '../../calendar/NoSlotsAvailablePopover';
import { useFormCalendarSync } from '../../calendar/useFormCalendarSync';
import {
  getMinimumNoticeHours,
  type MinimumNoticeSource,
} from '../../availability';
import {
  SlotsPreviewModal,
  type FindTimeApplyArgs,
  type FindTimeValue,
  type SelfScheduleHandle,
  type SlotsPreviewData,
} from '../../calendar/selfSchedule';

interface DetailsStepProps extends ScheduleStepProps {
  interviewPlan?: Record<string, unknown>;
  candidateName?: string;
  candidateAvatar?: string;
}

export const DetailsStep = memo(
  forwardRef<StepFormApi, DetailsStepProps>(
    (
      {
        payloadRef,
        mode,
        apiReference,
        prefillVersion = 0,
        interviewPlan,
        candidateName,
        candidateAvatar,
        candidatePhone,
        candidatePhoneValue,
        atsJobId,
        atsJobStageActionId,
        additionalSlotsContext,
      },
      ref
    ) => {
      const formRef = useRef<FormBuilderHandle>(null);
      const calendarRef = useRef<CalendarHandle>(null);
      const selfScheduleRef = useRef<SelfScheduleHandle>(null);
      const toast = useShowToast();
      const { state: sharedState, update: updateSharedState } =
        useScheduleSharedState();

      const isSelfSchedule = isSelfScheduleMode(mode);
      const { feInterviewBuffer } = useKFlags() as {
        feInterviewBuffer?: boolean;
      };
      const isBufferEnabledByFlag =
        Boolean(feInterviewBuffer) && isSelfSchedule;

      const handleBufferChange = useCallback(
        (next: { before: number; after: number; enabled: boolean }) => {
          updateSharedState({
            bufferBeforeMinutes: next.before,
            bufferAfterMinutes: next.after,
            bufferEnabled: next.enabled,
          });
        },
        [updateSharedState]
      );

      // ─── Find Time state (self-schedule only) ──────────────────
      // `findTimeValue` is null when the user hasn't picked a preset
      // or has manually edited after applying one (dirty tracking).
      // The walker anchors on today and doesn't take an availability
      // oracle — it picks target dates unconditionally and the
      // pipeline handles what's actually renderable.
      const [findTimeValue, setFindTimeValue] = useState<FindTimeValue>(null);

      // Suppresses the dirty-reset the next time `onSlotsChange`
      // fires — we set it right before dispatching `applyFindTime`
      // so the flood of state updates that follows doesn't null out
      // the user's just-picked preset.
      const ignoreNextSlotEmitRef = useRef(false);

      const handleFindTimeApply = useCallback(
        ({ value, targetDateKeys, firstDateKey }: FindTimeApplyArgs) => {
          ignoreNextSlotEmitRef.current = true;
          selfScheduleRef.current?.applyFindTime({ targetDateKeys });
          setFindTimeValue(value);
          // Always navigate to the first target's week when Find
          // Time is applied. Covers both cases:
          //   - user clicks "Find time this week" from a different
          //     week → calendar scrolls back to today's week
          //   - user clicks "Next week" from current week →
          //     calendar scrolls forward to that week
          // The self-schedule calendar's `goToDate` imperatively
          // calls FullCalendar's `gotoDate`, which moves to the
          // week containing the given date.
          if (firstDateKey) {
            selfScheduleRef.current?.goToDate(firstDateKey);
          }
        },
        []
      );

      const handleFindTimeClear = useCallback(() => {
        ignoreNextSlotEmitRef.current = true;
        selfScheduleRef.current?.clearAll();
        setFindTimeValue(null);
      }, []);

      // Preview modal — opened from the FindTimeField's "Preview"
      // link, sends the current slot set + interview metadata into
      // the candidate-app iframe via postMessage (see
      // `SlotsPreviewModal`).
      const [isPreviewOpen, setPreviewOpen] = useState(false);
      const handlePreviewOpen = useCallback(() => setPreviewOpen(true), []);
      const handlePreviewClose = useCallback(() => setPreviewOpen(false), []);

      // The candidate app expects slots keyed by date with `start_time`/`end_time`
      // fields — mirrors legacy `processSelectedSlots`. Our flat array needs
      // to be grouped before being posted into the iframe.
      const selfScheduleSlotsForPreview = useMemo(() => {
        const result: Record<
          string,
          Array<{ start_time: string; end_time: string }>
        > = {};
        for (const slot of sharedState.selfScheduleSlots ?? []) {
          const dateKey = slot.start.split('T')[0];
          if (!result[dateKey]) result[dateKey] = [];
          result[dateKey].push({ start_time: slot.start, end_time: slot.end });
        }
        return result;
      }, [sharedState.selfScheduleSlots]);

      // Multi-week availability flowing from the calendar's pipeline
      // — `YYYY-MM-DD` keys for every day with at least one bookable
      // slot. Drives the Find Time walker so picks land on real
      // availability instead of the hardcoded Mon-Fri set. `undefined`
      // until the first emit; walker falls back to legacy weekday
      // behaviour while it's missing.
      const [availableDays, setAvailableDays] = useState<
        ReadonlySet<string> | undefined
      >(undefined);
      const handleAvailableDaysChange = useCallback((keys: string[]) => {
        // Bail when the new keys match current content. Without this,
        // every emit creates a fresh Set whose reference inequality
        // re-renders DetailsStep → CalendarPanel → SelfScheduleCalendar,
        // and the calendar's `useEffect` re-emits the same keys, looping
        // forever and breaking FC mid-drag (resetOptions wipes the
        // in-flight drag's `validMutation` so `eventDrop` never fires).
        setAvailableDays((prev) => {
          if (
            prev &&
            prev.size === keys.length &&
            keys.every((k) => prev.has(k))
          ) {
            return prev;
          }
          return new Set(keys);
        });
      }, []);

      // Fresh key per Find-Time-no-availability event remounts the
      // popover so its internal "dismissed" state resets — without it
      // a user who dismissed once wouldn't see the next prompt.
      const [noAvailabilityKey, setNoAvailabilityKey] = useState<string | null>(
        null
      );
      const handleFindTimeNoAvailability = useCallback(
        (mode: FindTimeValue) => {
          setNoAvailabilityKey(`${mode ?? 'unknown'}-${Date.now()}`);
        },
        []
      );

      // Snapshot of the inputs the payload mapper needs to classify
      // each self-schedule slot's `is_custom` / `override_type` —
      // pushed up by `CalendarPanel.onScheduleContextChange` whenever
      // either side changes (gated on the panel side via content key
      // so it doesn't re-render us). Lives in a ref because we only
      // read it at save time inside `syncToPayload`; storing in state
      // would re-render the whole tree on every busy refetch for no
      // visual benefit.
      const scheduleContextRef = useRef<{
        workingHoursByInterviewer: Record<
          string,
          Array<{ start: string; end: string }>
        >;
        busyEvents: ReadonlyArray<{
          id: string;
          start: string;
          end: string;
          interviewerId: string | number;
        }>;
      }>({
        workingHoursByInterviewer: {},
        busyEvents: [],
      });
      const handleScheduleContextChange = useCallback(
        (ctx: {
          workingHoursByInterviewer: Record<
            string,
            Array<{ start: string; end: string }>
          >;
          busyEvents: ReadonlyArray<{
            id: string;
            start: string;
            end: string;
            interviewerId: string | number;
          }>;
        }) => {
          scheduleContextRef.current = ctx;
        },
        []
      );

      // Edit-mode seed for the self-schedule calendar's reducer.
      // Read synchronously from `payloadRef` so `useReducer`'s lazy
      // init picks them up on first render — routing through shared
      // state alone would arrive a tick late and the reducer would
      // already be seeded empty.
      const initialSelfScheduleSlots = useMemo<
        | Array<{
            start: string;
            end: string;
            isBooked?: boolean;
            is_custom?: boolean;
            override_type?: 'none' | 'calendar' | 'working_hours' | 'all';
          }>
        | undefined
      >(() => {
        if (apiReference !== 'edit' || !isSelfSchedule) return undefined;
        const slots = (payloadRef.current.details as Record<string, unknown>)
          .selfScheduleSlots as
          | Array<{
              start: string;
              end: string;
              is_custom?: boolean;
              override_type?: 'none' | 'calendar' | 'working_hours' | 'all';
            }>
          | undefined;
        if (!Array.isArray(slots) || slots.length === 0) return undefined;
        const bookedStartMs = additionalSlotsContext
          ? Date.parse(additionalSlotsContext.bookedSlot.start)
          : Number.NaN;
        const bookedEndMs = additionalSlotsContext
          ? Date.parse(additionalSlotsContext.bookedSlot.end)
          : Number.NaN;
        // Forward `is_custom` / `override_type` from the hydrated
        // payload into the seed so the reducer can mark the seeded
        // pick `_fromServer` and the payload mapper preserves the
        // backend's classification on save (unless the user moves it).
        return slots.map((s) => {
          const isBooked =
            Number.isFinite(bookedStartMs) &&
            Number.isFinite(bookedEndMs) &&
            Date.parse(s.start) === bookedStartMs &&
            Date.parse(s.end) === bookedEndMs;
          return {
            start: s.start,
            end: s.end,
            ...(isBooked ? { isBooked: true } : {}),
            ...(s.is_custom !== undefined ? { is_custom: s.is_custom } : {}),
            ...(s.override_type ? { override_type: s.override_type } : {}),
          };
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [
        apiReference,
        isSelfSchedule,
        payloadRef,
        prefillVersion,
        additionalSlotsContext,
      ]);

      // Seed `findTimeValue` from the hydrated preset once on mount.
      const hydratedFindTimeRef = useRef(false);
      useEffect(() => {
        if (hydratedFindTimeRef.current) return;
        if (apiReference !== 'edit' || !isSelfSchedule) return;
        const ftv = (payloadRef.current.details as Record<string, unknown>)
          .findTimeValue as FindTimeValue | undefined;
        if (ftv) {
          hydratedFindTimeRef.current = true;
          setFindTimeValue(ftv);
        }
      }, [apiReference, isSelfSchedule, payloadRef, prefillVersion]);

      const { handleFieldChange } = useFormCalendarSync({
        formRef,
        sharedState,
        updateSharedState,
        calendarRef,
      });

      // Seed duration from the interview plan so edit mode + presets
      // reach the calendar overlay before the user touches the form.
      useEffect(() => {
        const planDuration =
          typeof interviewPlan?.duration === 'number' &&
          interviewPlan.duration > 0
            ? interviewPlan.duration
            : 30;
        if (planDuration !== sharedState.duration) {
          updateSharedState({ duration: planDuration });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [interviewPlan?.duration]);

      // Edit: seed sharedState from the hydrated payloadRef. Create
      // (with an interview plan): seed the same fields from the plan
      // so the calendar fires its busy-events fetch for the plan's
      // default interviewers — otherwise sharedState stays empty
      // until the user manually changes a field, and the calendar
      // shows a blank week even though the form already has the
      // interviewers prefilled.
      useEffect(() => {
        const updates: Partial<typeof sharedState> = {};
        if (apiReference === 'edit') {
          const details = payloadRef.current.details as Record<string, unknown>;
          const bs = details.bookedSlot as
            | { start?: string; end?: string; duration?: number }
            | undefined;
          if (bs?.start && bs?.end) {
            updates.bookedSlot = {
              start: bs.start,
              end: bs.end,
              duration: bs.duration ?? sharedState.duration,
            };
          }
          // Mirror the hydrated duration into top-level `sharedState.duration`
          // — the calendar's click-to-move handler reads it as the slot
          // length when the user clicks a 15-min cell to move the booked
          // event (`useCalendarEventHandlers.handleSelect` builds
          // `end = start + duration`). The plan-duration seed effect above
          // can't pick this up because GET `/ats_interviews/:id` does NOT
          // return a top-level `duration` field — it's computed from
          // `(end_time - start_time)` inside `hydrateDetailsFromApi` and
          // landed on `payloadRef.details.duration`. Without this mirror,
          // sharedState.duration stays at the default 30 and a 2-min
          // interview silently becomes 30 mins on first click-to-move.
          const detailsDuration = details.duration;
          if (
            typeof detailsDuration === 'number' &&
            detailsDuration > 0 &&
            detailsDuration !== sharedState.duration
          ) {
            updates.duration = detailsDuration;
          }
          // Same pattern for `interviewKind` — sharedState defaults to
          // `'one_on_one'` and the calendar's availability algorithm uses
          // it to pick union (one_on_one) vs intersection (panel) of
          // interviewers' free time. Without this seed, editing a panel
          // interview shows union availability until the user touches
          // the interview type field. `useFormCalendarSync` already
          // syncs subsequent user changes via the `interview_type` case.
          const detailsKind = details.interview_type;
          if (
            (detailsKind === 'panel' || detailsKind === 'one_on_one') &&
            detailsKind !== sharedState.interviewKind
          ) {
            updates.interviewKind = detailsKind;
          }
          // Self-schedule edit: seed shared state with the hydrated
          // time slots so the calendar's reducer sees them on first
          // paint. Without this the pre-selected slots silently fall
          // away — payloadRef has them, but the form-side sync only
          // picked up `bookedSlot` before.
          const slots = details.selfScheduleSlots as
            | Array<{
                start: string;
                end: string;
                duration?: number;
                is_custom?: boolean;
                override_type?: 'none' | 'calendar' | 'working_hours' | 'all';
              }>
            | undefined;
          if (
            isSelfSchedule &&
            Array.isArray(slots) &&
            slots.length > 0 &&
            (sharedState.selfScheduleSlots?.length ?? 0) === 0
          ) {
            // Mark hydrated slots as `_fromServer: true` so the payload
            // mapper preserves their override classification verbatim
            // until the user moves them. Stripping this would mean
            // every save recomputes — fine in theory, but legacy
            // preserves server values to avoid drift if the backend
            // classification logic differs from ours (it does — backend
            // has account-level working-hours overrides we don't model
            // here).
            updates.selfScheduleSlots = slots.map((s) => ({
              start: s.start,
              end: s.end,
              duration: s.duration ?? sharedState.duration,
              ...(s.is_custom !== undefined ? { is_custom: s.is_custom } : {}),
              ...(s.override_type ? { override_type: s.override_type } : {}),
              _fromServer: true,
            }));
          }
          const bb = details.bufferBeforeMinutes;
          const ba = details.bufferAfterMinutes;
          if (typeof bb === 'number') updates.bufferBeforeMinutes = bb;
          if (typeof ba === 'number') updates.bufferAfterMinutes = ba;
          const ids = details.participant_ids;
          if (Array.isArray(ids)) {
            updates.interviewerIds = ids.map(Number).filter(Number.isFinite);
          } else if (typeof ids === 'string' && ids) {
            const n = Number(ids);
            if (Number.isFinite(n)) updates.interviewerIds = [n];
          }
          const rooms = details.meeting_room_ids;
          if (Array.isArray(rooms)) {
            updates.meetingRoomEmails = rooms.map(String).filter(Boolean);
          }
          const tz = details.timezone;
          if (typeof tz === 'string' && tz) updates.timezone = tz;
          const name = details.interviewName;
          if (typeof name === 'string' && name) {
            updates.interviewName = name;
            updates.originalInterviewName = name;
          }
        } else if (interviewPlan) {
          // Create with plan: read from payloadRef.details (already mapped
          // to form field names by hydratePlanDefaults) — same field names
          // as the edit branch above so guards and reads are consistent.
          const planDetails = payloadRef.current.details as Record<
            string,
            unknown
          >;
          const rawIds = planDetails.participant_ids;
          const allIds = (
            Array.isArray(rawIds) ? rawIds : rawIds ? [rawIds] : []
          )
            .map((id) => Number(id))
            .filter((n) => Number.isFinite(n) && n > 0);

          // For a one-on-one (manual mode), the plan's participants list
          // is a POOL — only ONE attends. Limit the calendar feed to the
          // first so it doesn't show busy blocks for all pool members.
          // Panel + self-schedule send everyone (all attend).
          const planKind = planDetails.interview_type as string | undefined;
          const useAllIds = planKind === 'panel' || isSelfSchedule;
          const idsForCalendar = useAllIds ? allIds : allIds.slice(0, 1);

          if (
            idsForCalendar.length > 0 &&
            sharedState.interviewerIds.length === 0
          ) {
            updates.interviewerIds = idsForCalendar;
          }

          const rooms = planDetails.meeting_room_ids as string[] | undefined;
          if (
            Array.isArray(rooms) &&
            rooms.length > 0 &&
            sharedState.meetingRoomEmails.length === 0
          ) {
            updates.meetingRoomEmails = rooms;
          }

          const planName = planDetails.interviewName as string | undefined;
          if (
            typeof planName === 'string' &&
            planName &&
            !sharedState.interviewName
          ) {
            updates.interviewName = planName;
          }

          if (
            (planKind === 'panel' || planKind === 'one_on_one') &&
            sharedState.interviewKind !== planKind
          ) {
            updates.interviewKind = planKind;
          }
        }
        if (Object.keys(updates).length > 0) {
          updateSharedState(updates);
        }
        // Only seed on (re)mount keyed by prefillVersion (edit) or
        // when the plan becomes available (create). `updateSharedState`
        // is stable; depending on `sharedState` would loop.
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [apiReference, prefillVersion, interviewPlan]);

      const handleVisibleWeekStartChange = useCallback(
        (iso: string) => {
          updateSharedState({ calendarWeekStart: iso });
        },
        [updateSharedState]
      );

      const handleCalendarAvailabilityChange = useCallback(
        (disconnectedIds: number[]) => {
          updateSharedState({
            calendarDisconnectedInterviewerIds: disconnectedIds,
          });
        },
        [updateSharedState]
      );

      const handleSlotSelect = useCallback(
        (slot: { start: string; end: string }) => {
          // Manual-only path (self-schedule's slot collection flows
          // through `handleSlotsChange`). The dragged-slot bounds are
          // authoritative — derive duration from them so a drag-to-
          // resize on the calendar reflects on the form's Duration
          // field. Click selection keeps the existing duration
          // because `useCalendarEventHandlers.handleSelect` already
          // builds `end = start + duration` for clicks; only drags
          // produce a different span.
          const startMs = new Date(slot.start).getTime();
          const endMs = new Date(slot.end).getTime();
          const slotDuration = Math.max(
            1,
            Math.round((endMs - startMs) / 60000)
          );
          updateSharedState({
            bookedSlot: {
              start: slot.start,
              end: slot.end,
              duration: slotDuration,
            },
            duration: slotDuration,
          });
        },
        [updateSharedState]
      );

      // Stable callback so DetailsForm's effect doesn't re-fire every
      // render and trigger the shared-store to re-notify listeners in
      // a loop.
      const handleMeetingRoomNamesChange = useCallback(
        (namesByEmail: Record<string, string>) => {
          updateSharedState({ meetingRoomNamesByEmail: namesByEmail });
        },
        [updateSharedState]
      );

      const handleSlotsChange = useCallback(
        (
          slots: Array<{
            start: string;
            end: string;
            is_custom?: boolean;
            override_type?: 'none' | 'calendar' | 'working_hours' | 'all';
            _fromServer?: boolean;
          }>
        ) => {
          updateSharedState({
            // Preserve override flags + `_fromServer` gate from the
            // calendar emit. `mapDetails` reads these at save time and
            // either passes them through (server-sourced + unmoved) or
            // recomputes via `computeSlotOverride`. Stripping them here
            // would silently send `is_custom: false` for legitimate
            // override picks, making them unbookable by candidates
            // (backend's working-hours / busy-event filters reject the
            // booking attempt). Same parity gap that motivated this
            // whole pass.
            selfScheduleSlots: slots.map((s) => ({
              start: s.start,
              end: s.end,
              duration: sharedState.duration,
              ...(s.is_custom !== undefined ? { is_custom: s.is_custom } : {}),
              ...(s.override_type ? { override_type: s.override_type } : {}),
              ...(s._fromServer ? { _fromServer: true } : {}),
            })),
          });
          // Dirty-track the Find Time selection. Any emit that isn't
          // the one triggered by our own `applyFindTime`/`clearAll`
          // call (those set `ignoreNextSlotEmitRef = true`) is a
          // manual edit and should null out the active preset.
          if (ignoreNextSlotEmitRef.current) {
            ignoreNextSlotEmitRef.current = false;
          } else if (findTimeValue !== null) {
            setFindTimeValue(null);
          }
        },
        [findTimeValue, sharedState.duration, updateSharedState]
      );

      useImperativeHandle(
        ref,
        () => ({
          validate: async () => {
            if (!formRef.current) return true;
            const slot = sharedState.bookedSlot;
            const needsSlot = !isSelfSchedule && (!slot?.start || !slot?.end);
            // Self-schedule's analogue: at least one picked slot
            // before moving on. Mirrors manual's toast so the user
            // gets the same failure signal on both modes.
            const needsSelfSlots =
              isSelfSchedule &&
              (sharedState.selfScheduleSlots?.length ?? 0) === 0;
            if (needsSlot) {
              toast({
                id: 'schedule-interview-no-slot',
                title: 'Pick a time slot on the calendar',
                status: 'error',
              });
            } else if (needsSelfSlots) {
              toast({
                id: 'schedule-interview-no-self-slots',
                title: 'Pick at least one slot on the calendar',
                status: 'error',
              });
            }
            const isFormValid = await formRef.current.trigger();
            return isFormValid && !needsSlot && !needsSelfSlots;
          },
          syncToPayload: () => {
            if (!formRef.current) return;
            const formValues = formRef.current.getValues();
            const shouldEmitBuffer =
              isBufferEnabledByFlag && sharedState.bufferEnabled;
            // Edit-mode `interviewPlan === existingInterview` (the raw
            // GET response), so its `meeting_rooms` is the
            // server-current room list. mapDetails uses it to diff
            // against `meeting_room_ids` and emit `_destroy: true` for
            // removed entries — the parity gap audit caught this gap
            // (legacy did the diff; the new wizard was only emitting
            // additions, so removed rooms stayed attached server-side).
            // Create mode → no `meeting_rooms` on the plan → mapDetails
            // skips deletion logic and behaves as before.
            const originalMeetingRooms = Array.isArray(
              (interviewPlan as { meeting_rooms?: unknown })?.meeting_rooms
            )
              ? ((interviewPlan as { meeting_rooms: unknown[] })
                  .meeting_rooms as Array<{
                  id?: number | string;
                  email?: string;
                  name?: string;
                }>)
              : [];
            payloadRef.current.details = {
              ...formValues,
              bookedSlot: sharedState.bookedSlot,
              selfScheduleSlots: sharedState.selfScheduleSlots,
              timezone: sharedState.timezone,
              shared_calendar_enabled: Boolean(
                interviewPlan?.shared_calendar_enabled
              ),
              // Round-tripped into `schedule_type` by the payload
              // builder (`scheduleTypeFor`). `null` → 'Custom'.
              findTimeValue,
              // Plan-level meeting-rooms toggle — drives the "feature
              // disabled → wipe all rooms" branch in mapDetails.
              meeting_rooms_enabled:
                (interviewPlan as { meeting_rooms_enabled?: unknown })
                  ?.meeting_rooms_enabled === undefined
                  ? true
                  : Boolean(
                      (interviewPlan as { meeting_rooms_enabled?: unknown })
                        .meeting_rooms_enabled
                    ),
              // Snapshot of server-side rooms — UI-only key consumed by
              // mapDetails for the deletion diff. Stripped before wire
              // because mapDetails builds `mapped` key-by-key.
              _originalMeetingRooms: originalMeetingRooms,
              // Self-schedule only — snapshot the override-classifier
              // inputs into the details payload so `mapDetails` can
              // compute `is_custom` / `override_type` per slot at save
              // time. `_overrideContext` is a UI-only key; the payload
              // builder reads + strips it before serialising. See
              // `payload/computeSlotOverride.ts` for the algorithm.
              ...(isSelfSchedule
                ? {
                    _overrideContext: {
                      workingHoursByInterviewer:
                        scheduleContextRef.current.workingHoursByInterviewer,
                      busyEvents: scheduleContextRef.current.busyEvents,
                      interviewerIds: sharedState.interviewerIds,
                      interviewKind: sharedState.interviewKind,
                    },
                  }
                : {}),
              ...(shouldEmitBuffer
                ? {
                    bufferBeforeMinutes: sharedState.bufferBeforeMinutes,
                    bufferAfterMinutes: sharedState.bufferAfterMinutes,
                  }
                : {}),
            };
          },
        }),
        [
          payloadRef,
          sharedState.bookedSlot,
          sharedState.selfScheduleSlots,
          sharedState.timezone,
          sharedState.bufferEnabled,
          sharedState.bufferBeforeMinutes,
          sharedState.bufferAfterMinutes,
          sharedState.interviewerIds,
          sharedState.interviewKind,
          interviewPlan,
          isSelfSchedule,
          isBufferEnabledByFlag,
          toast,
          findTimeValue,
        ]
      );

      return (
        <Flex
          key={String(prefillVersion)}
          w="100%"
          h="100%"
          minH={0}
          flex="1"
          gap="0"
        >
          {/* Left panel — Calendar */}
          <Box
            position="relative"
            flex="3"
            minW={0}
            h="100%"
            borderRight="1px solid"
            borderColor="border-color"
            overflow="hidden"
          >
            <CalendarPanel
              ref={calendarRef}
              mode={mode}
              interviewKind={sharedState.interviewKind}
              prefetchWeeks={3}
              duration={sharedState.duration}
              timezone={sharedState.timezone}
              interviewerIds={sharedState.interviewerIds.map(String)}
              meetingRoomIds={sharedState.meetingRoomIds.map(String)}
              meetingRoomEmails={sharedState.meetingRoomEmails}
              meetingRoomNamesByEmail={sharedState.meetingRoomNamesByEmail}
              interviewName={sharedState.interviewName}
              originalInterviewName={sharedState.originalInterviewName}
              candidateName={candidateName}
              bookedSlot={sharedState.bookedSlot}
              onSlotSelect={handleSlotSelect}
              onTimezoneChange={(tz) => updateSharedState({ timezone: tz })}
              onSlotsChange={handleSlotsChange}
              onVisibleWeekStartChange={handleVisibleWeekStartChange}
              onCalendarAvailabilityChange={handleCalendarAvailabilityChange}
              onAvailableDaysChange={handleAvailableDaysChange}
              onScheduleContextChange={handleScheduleContextChange}
              isEdit={apiReference === 'edit'}
              atsJobId={atsJobId}
              minimumNoticeHours={getMinimumNoticeHours(
                interviewPlan as MinimumNoticeSource | undefined
              )}
              selfScheduleRef={selfScheduleRef}
              initialSelfScheduleSlots={initialSelfScheduleSlots}
              bufferBeforeMinutes={
                isBufferEnabledByFlag && sharedState.bufferEnabled
                  ? sharedState.bufferBeforeMinutes
                  : 0
              }
              bufferAfterMinutes={
                isBufferEnabledByFlag && sharedState.bufferEnabled
                  ? sharedState.bufferAfterMinutes
                  : 0
              }
            />
            {/* Find-Time "selected interviewers not available" popover.
                 Mounted here so the absolute-positioned popover overlays
                 the calendar grid (same visual home as the existing
                 "no slots this week" variant). Each fire sets a fresh
                 key, remounting the dismissable popover. */}
            {noAvailabilityKey && (
              <NoSlotsAvailablePopover
                key={noAvailabilityKey}
                visible
                reason="find-time"
              />
            )}
          </Box>

          {/* Right panel — Form */}
          <Box
            w="400px"
            maxW="400px"
            minW="400px"
            flexShrink={0}
            h="100%"
            overflowY="auto"
            px="5"
            py="4"
          >
            <DetailsForm
              ref={formRef}
              mode={mode}
              apiReference={apiReference}
              payloadRef={payloadRef}
              interviewPlan={interviewPlan}
              calendarBookedStartIso={sharedState.bookedSlot?.start ?? null}
              calendarBookedEndIso={sharedState.bookedSlot?.end ?? null}
              visibleWeekStartIso={sharedState.calendarWeekStart || null}
              disconnectedInterviewerIds={
                sharedState.calendarDisconnectedInterviewerIds
              }
              scheduleTimezone={sharedState.timezone}
              candidateName={candidateName}
              candidateAvatar={candidateAvatar}
              candidatePhone={candidatePhone}
              candidatePhoneValue={candidatePhoneValue}
              atsJobStageActionId={atsJobStageActionId}
              // Stops the AI-recommended-interviewer query as soon as
              // the user picks anyone — `useFormCalendarSync` mirrors
              // `participant_ids` into `sharedState.interviewerIds` on
              // every form change, so this flips before the next
              // render of the schema. Without it, dragging the booked
              // slot kept refetching the stale recommendation.
              hasInterviewerSelected={sharedState.interviewerIds.length > 0}
              onFieldChange={handleFieldChange}
              onMeetingRoomNamesChange={handleMeetingRoomNamesChange}
              // Find Time — only passed in self-schedule modes. The
              // schema renders a custom row below Duration that
              // mounts `<FindTimeField>` and hides itself until at
              // least one interviewer is selected (legacy parity).
              findTimeProps={
                isSelfSchedule
                  ? {
                      value: findTimeValue,
                      slotsCount: sharedState.selfScheduleSlots?.length ?? 0,
                      timezone: sharedState.timezone ?? '',
                      minimumNoticeHours: getMinimumNoticeHours(
                        interviewPlan as MinimumNoticeSource | undefined
                      ),
                      availableDays,
                      onApply: handleFindTimeApply,
                      onClear: handleFindTimeClear,
                      onPreview: handlePreviewOpen,
                      onNoAvailability: handleFindTimeNoAvailability,
                    }
                  : undefined
              }
              bufferProps={
                isBufferEnabledByFlag
                  ? {
                      value: {
                        before: sharedState.bufferBeforeMinutes,
                        after: sharedState.bufferAfterMinutes,
                        enabled: sharedState.bufferEnabled,
                      },
                      onChange: handleBufferChange,
                    }
                  : undefined
              }
            />
          </Box>
          {isSelfSchedule ? (
            <SlotsPreviewModal
              isOpen={isPreviewOpen}
              onClose={handlePreviewClose}
              data={
                {
                  src: 'kula',
                  styleProps: {},
                  name: sharedState.interviewName,
                  duration: sharedState.duration,
                  timeZone: sharedState.timezone,
                  location_type: (formRef.current?.getValues?.()?.location ??
                    'google_meet') as string,
                  contact_name: candidateName,
                  slots: selfScheduleSlotsForPreview,
                } satisfies SlotsPreviewData
              }
            />
          ) : null}
        </Flex>
      );
    }
  )
);

DetailsStep.displayName = 'DetailsStep';
